import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Star, Pencil } from 'lucide-react';
export const Button = ({
  children,
  className = '',
  variant = 'primary',
  size = 'lg',
  onClick,
  href
}: {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'green';
  size?: 'lg' | 'xl';
  onClick?: () => void;
  href?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-bold rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95';
  const variants = {
    primary: 'bg-brand-pink text-white shadow-pink hover:bg-pink-500',
    secondary: 'bg-brand-blue text-white shadow-blue hover:bg-blue-500',
    green: 'bg-brand-green text-white shadow-soft hover:bg-green-500'
  };
  const sizes = {
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl md:text-2xl w-full md:w-auto'
  };
  const combinedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;
  if (href) {
    return <motion.a href={href} target="_blank" rel="noopener noreferrer" whileHover={{
      y: -2
    }} whileTap={{
      scale: 0.98
    }} className={combinedClassName} onClick={onClick} data-id="element-313">
        {children}
      </motion.a>;
  }
  return <motion.button whileHover={{
    y: -2
  }} whileTap={{
    scale: 0.98
  }} className={combinedClassName} onClick={onClick} data-id="element-314">
      {children}
    </motion.button>;
};
export const SectionHeading = ({
  children,
  className = '',
  subtitle
}: {
  children: React.ReactNode;
  className?: string;
  subtitle?: string;
}) => <div className={`text-center mb-12 md:mb-16 ${className}`} data-id="element-315">
    <motion.h2 initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="font-heading text-4xl md:text-5xl text-brand-navy mb-4" data-id="element-316">
      {children}
    </motion.h2>
    {subtitle && <motion.p initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.1
  }} className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto" data-id="element-317">
        {subtitle}
      </motion.p>}
  </div>;
export const Sticker = ({
  type,
  className,
  delay = 0
}: {
  type: 'sparkle' | 'star' | 'pencil';
  className: string;
  delay?: number;
}) => {
  const icons = {
    sparkle: <Sparkles className="w-8 h-8 text-brand-yellow fill-brand-yellow" data-id="element-318" />,
    star: <Star className="w-8 h-8 text-brand-pink fill-brand-pink" data-id="element-319" />,
    pencil: <Pencil className="w-8 h-8 text-brand-blue fill-brand-blue" data-id="element-320" />
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay,
    duration: 0.5,
    type: 'spring'
  }} className={`absolute z-10 ${className}`} data-id="element-321">
      <motion.div animate={{
      y: [0, -10, 0],
      rotate: [-5, 5, -5]
    }} transition={{
      repeat: Infinity,
      duration: 3,
      delay
    }} data-id="element-322">
        {icons[type]}
      </motion.div>
    </motion.div>;
};